import { useCallback, useEffect, useState } from 'react'

import { ApiError } from '../../lib/api'
import { biz, type Contact, type Lead, type PipelineColumn } from '../../lib/business'
import { money } from '../../lib/format'
import { useToast } from '../../app/Toasts'
import { Field } from './ContactsPage'

export default function PipelinePage() {
  const { push } = useToast()
  const [cols, setCols] = useState<PipelineColumn[]>([])
  const [contacts, setContacts] = useState<Contact[]>([])
  const [loading, setLoading] = useState(true)
  const [creating, setCreating] = useState(false)
  const [dragging, setDragging] = useState<string | null>(null)
  const [draft, setDraft] = useState({
    title: '', contact_id: '', expected_value: 0, source: '', contact_name: '',
  })

  const load = useCallback(async () => {
    try { setCols((await biz.pipeline()).stages) }
    catch { /* ignore */ } finally { setLoading(false) }
  }, [])

  useEffect(() => {
    load()
    biz.contacts({ role: 'customer' }).then((r) => setContacts(r.items)).catch(() => {})
  }, [load])

  const move = async (leadId: string, stageId: string) => {
    try {
      await biz.moveLead(leadId, stageId)
      load()
    } catch (e) {
      push('error', 'Could not move the lead',
        e instanceof ApiError ? e.message : undefined)
    }
  }

  const create = async () => {
    try {
      await biz.createLead({
        title: draft.title,
        contact_id: draft.contact_id || null,
        contact_name: draft.contact_name || null,
        expected_value: draft.expected_value || null,
        source: draft.source || null,
      } as Partial<Lead> & { title: string })
      push('success', 'Lead created')
      setCreating(false)
      setDraft({ title: '', contact_id: '', expected_value: 0, source: '', contact_name: '' })
      load()
    } catch (e) {
      push('error', 'Could not create the lead',
        e instanceof ApiError ? e.message : undefined)
    }
  }

  if (loading) return <div className="skeleton" style={{ height: 300 }} />

  const totalWeighted = cols.reduce((sum, c) => sum + (c.stage.is_lost ? 0 : c.weighted_value), 0)

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <div className="card">
        <div className="card-head">
          <span className="card-title">Sales pipeline</span>
          <div className="grow" />
          <span className="muted" style={{ fontSize: 12.5 }}>
            weighted forecast <strong style={{ color: 'var(--text)' }}>{money(totalWeighted)}</strong>
          </span>
          <button className="btn btn-sm btn-primary" onClick={() => setCreating(true)}>
            New lead
          </button>
        </div>
      </div>

      {creating && (
        <div className="card">
          <div className="card-head"><span className="card-title">New lead</span></div>
          <div className="card-body">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <Field label="Title" required>
                <input className="input" value={draft.title}
                  onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                  placeholder="Bulk fastener supply — Q3" />
              </Field>
              <Field label="Existing customer">
                <select className="input" value={draft.contact_id}
                  onChange={(e) => setDraft({ ...draft, contact_id: e.target.value })}>
                  <option value="">— not yet a customer —</option>
                  {contacts.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </Field>
              {!draft.contact_id && (
                <Field label="Contact name" hint="Promoted to a customer when the lead is won">
                  <input className="input" value={draft.contact_name}
                    onChange={(e) => setDraft({ ...draft, contact_name: e.target.value })} />
                </Field>
              )}
              <Field label="Expected value">
                <input className="input" type="number" value={draft.expected_value}
                  onChange={(e) => setDraft({ ...draft, expected_value: Number(e.target.value) })} />
              </Field>
              <Field label="Source">
                <input className="input" value={draft.source} placeholder="referral, website, call"
                  onChange={(e) => setDraft({ ...draft, source: e.target.value })} />
              </Field>
            </div>
            <div className="row" style={{ marginTop: 14 }}>
              <button className="btn btn-primary" onClick={create} disabled={!draft.title}>
                Create lead
              </button>
              <button className="btn" onClick={() => setCreating(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      <div className="board">
        {cols.map((col) => (
          <div
            key={col.stage.id}
            className={`board-col${dragging ? ' droppable' : ''}`}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault()
              const leadId = e.dataTransfer.getData('text/plain')
              if (leadId) move(leadId, col.stage.id)
              setDragging(null)
            }}
          >
            <div className="board-head">
              <span style={{ fontWeight: 600, fontSize: 12.5 }}>{col.stage.name}</span>
              <span className="nav-count">{col.count}</span>
            </div>
            <div className="muted" style={{ fontSize: 11, padding: '0 2px 8px' }}>
              {money(col.value)}
              {!col.stage.is_won && !col.stage.is_lost && col.weighted_value > 0 &&
                ` · ${money(col.weighted_value)} weighted`}
            </div>

            {col.leads.map((lead) => (
              <div
                key={lead.id}
                className="lead-card"
                draggable
                onDragStart={(e) => {
                  e.dataTransfer.setData('text/plain', lead.id)
                  setDragging(lead.id)
                }}
                onDragEnd={() => setDragging(null)}
              >
                <div style={{ fontWeight: 550, fontSize: 12.5 }}>{lead.title}</div>
                <div className="muted" style={{ fontSize: 11.5 }}>
                  {lead.contact?.name ?? lead.contact_name ?? 'No contact'}
                </div>
                <div className="row" style={{ marginTop: 5, justifyContent: 'space-between' }}>
                  <span className="num" style={{ fontSize: 12 }}>
                    {lead.expected_value ? money(lead.expected_value) : '—'}
                  </span>
                  <span className="pill pill-mute">{lead.probability}%</span>
                </div>
              </div>
            ))}

            {col.leads.length === 0 && (
              <div className="muted" style={{ fontSize: 11.5, padding: '10px 4px', textAlign: 'center' }}>
                Drop a lead here
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}
